/**
 * A date box and a time box, side by side — the replacement for `<input type="datetime-local">`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS (F-14, raised by the Technical Delegate on 17 August 2026)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `datetime-local` looks like one control and behaves like two that fight each other. On the
 * browsers the TD actually uses, moving to the time half re-opens the calendar over the field
 * he is typing into, and the whole control is a single tab stop whose internal segments move
 * on arrow keys nobody guesses. Recording the moment an incident happened is not the place to
 * make a Technical Delegate fight a date picker.
 *
 * So the moment is asked for as **two plain native inputs** — `type="date"` and `type="time"`
 * — with their own labels, their own tab stops, and no shared popup. Setting the time cannot
 * open the calendar, because the time box is not the calendar's box.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE HALF-FILLED CASE IS A REAL STATE AND IS REPORTED AS ONE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Two boxes can hold half a moment, which one box could not. {@link combineDateAndTime}
 * returns `null` for *empty* — both boxes blank, which is a legitimate "not given" on an
 * optional field — and the string `"partial"` for a date with no time or a time with no date.
 * A caller must distinguish them: "you have not answered" and "you have answered half" need
 * different messages, and silently dropping a half answer would record the wrong instant or
 * none at all without saying so.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE BOXES ARE THE TOURNAMENT'S CLOCK, BOTH WAYS (RUN 83)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The two boxes hold a **wall-clock reading in the host city** — that is what the TD is being
 * asked for, and it is what the record is read back as: `caseText.ts` renders `incidentAt`,
 * `reportReceivedAt` and `hearingAt` in Asia/Tokyo on the case file, every generated document
 * does the same, and the 24-hour appeal clock is counted from the first of them.
 *
 * Until RUN 83 the pair was read in the DEVICE's zone at both ends — `new Date("2026-09-23T20:00")`
 * on the way in, `getHours()` on the way out. On a laptop set to Tokyo that is indistinguishable
 * from correct, which is why it survived: the suite ran on such a machine. On a laptop set to Los
 * Angeles, a TD typing `23 Sep 20:00` recorded `2026-09-24T03:00:00Z`, and the case file he had
 * just filled in read it back to him as **24 Sep 12:00** — sixteen hours out, on the wrong day,
 * with a charge clock started from it.
 *
 * So both directions go through `app/tournamentTime.ts`, and the property that matters is that
 * they are inverses: what {@link splitInstant} puts in the boxes, {@link combineDateAndTime}
 * turns back into the instant it came from, whatever the device is set to.
 */

import {
  instantFromTournamentTime,
  tournamentDay,
  tournamentTimeOfDay,
} from "../../app/tournamentTime.js";

/** What a date box and a time box can add up to. */
export type CombinedInstant =
  /** Both boxes blank — nothing was given, which on an optional field is an answer. */
  | { kind: "empty" }
  /** One box filled and the other blank — the caller must ask for the missing half. */
  | { kind: "partial"; missing: "date" | "time" }
  /** Both boxes filled and the pair parses — the instant they name on the tournament's clock. */
  | { kind: "instant"; iso: string }
  /** Both boxes filled and the pair does not parse. Rare, but never guessed at. */
  | { kind: "invalid" };

/**
 * `2026-09-23` + `20:00` → the instant that reading names in the host city —
 * `2026-09-23T11:00:00.000Z`.
 *
 * **RUN 83.** The two used to be joined with `T` and handed to `Date`, which reads an
 * offset-less local date-time in the DEVICE's zone. That was inherited from `datetime-local`
 * and was wrong in both: the boxes say what a clock in the host city showed, and the record is
 * read back on that clock. See the module note for what the TD saw.
 */
export function combineDateAndTime(date: string, time: string): CombinedInstant {
  if (date === "" && time === "") return { kind: "empty" };
  if (date === "") return { kind: "partial", missing: "date" };
  if (time === "") return { kind: "partial", missing: "time" };
  const parsed = instantFromTournamentTime(date, time);
  if (parsed === null) return { kind: "invalid" };
  return { kind: "instant", iso: parsed.toISOString() };
}

/**
 * An ISO instant back into the two boxes, for a form that opens on an existing value — the
 * exact inverse of {@link combineDateAndTime}, and on the same clock.
 *
 * **RUN 83.** It was `getFullYear()`/`getHours()` and their siblings, so a stored instant was
 * cut up in the DEVICE's zone: a hearing at 14:00 in Tokyo opened for editing showing `22:00`
 * on the previous day to a TD in Los Angeles, and saving the form unchanged would then have
 * moved it.
 */
export function splitInstant(iso: string | null): { date: string; time: string } {
  if (iso === null) return { date: "", time: "" };
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return { date: "", time: "" };
  return { date: tournamentDay(parsed), time: tournamentTimeOfDay(parsed) };
}

export interface DateTimeFieldProps {
  /** The visible heading over the pair, e.g. `Time Of The Incident`. */
  label: string;
  /** One short line under the heading saying what moment is being asked for. */
  hint?: string;
  date: string;
  time: string;
  onChangeDate: (value: string) => void;
  onChangeTime: (value: string) => void;
  /** Set when this field is one of the ones the TD must fix — draws the red border. */
  invalid?: boolean;
  /** The message under the field, when it is one of the offending ones (F-15). */
  message?: string | undefined;
  /** Focus lands on the DATE box, which is the first thing to fill in (F-15). */
  dateRef?: React.RefObject<HTMLInputElement | null> | undefined;
}

export function DateTimeField({
  label,
  hint,
  date,
  time,
  onChangeDate,
  onChangeTime,
  invalid = false,
  message,
  dateRef,
}: DateTimeFieldProps): React.JSX.Element {
  const messageId = `${label.replace(/\W+/g, "-").toLowerCase()}-msg`;
  return (
    <div className={invalid ? "field field--bad" : "field"}>
      <span className="field__label">{label}</span>
      {hint === undefined ? null : <span className="field__sub">{hint}</span>}
      <div className="datetimepair">
        <input
          type="date"
          aria-label={`${label} — date`}
          aria-invalid={invalid || undefined}
          aria-describedby={message === undefined ? undefined : messageId}
          ref={dateRef ?? null}
          value={date}
          onChange={(event) => {
            onChangeDate(event.target.value);
          }}
        />
        <input
          type="time"
          aria-label={`${label} — time`}
          aria-invalid={invalid || undefined}
          value={time}
          onChange={(event) => {
            onChangeTime(event.target.value);
          }}
        />
      </div>
      {message === undefined ? null : (
        <span className="field__bad" id={messageId} role="alert">
          {message}
        </span>
      )}
    </div>
  );
}
